import { useRef } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import Iconify from "@/components/iconify/iconify";

/** Verbs that configure a run rather than doing anything to a climber. */
const DIRECTIVE_VERBS = new Set(["mode", "product", "facet"]);

/**
 * Count the lines that will actually do something. Mirrors the server's own
 * count (which decides whether the batch fits the worker's timeout): blank
 * lines, comments and the directives above don't count.
 */
export const countManifestActionLines = (text: string): number => {
  const actionLines = text
    .split(/\r?\n/)
    .map((line) => line.split("#")[0].trim())
    .filter((line) => line && !DIRECTIVE_VERBS.has(line.split(/\s+/)[0]));
  return actionLines.length;
};

interface ManifestInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const ManifestInput = ({
  value,
  onChange,
  disabled,
}: ManifestInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const actionLines = countManifestActionLines(value);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result ?? ""));
    reader.readAsText(file);
    // Reset so picking the same file again still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Manifest
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Generate one with{" "}
        <Box component="code" sx={{ fontFamily: "monospace" }}>
          pnpm qa-climber manifest generate
        </Box>
        , prune it, then upload or paste it here.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Iconify icon="mdi:upload" />}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          Upload manifest
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,text/plain"
          style={{ display: "none" }}
          onChange={handleFileUpload}
        />
        <Typography variant="body2" color="text.secondary">
          {actionLines
            ? `${actionLines} action line${actionLines === 1 ? "" : "s"}`
            : "No action lines yet"}
        </Typography>
      </Box>

      <TextField
        label="Manifest text"
        fullWidth
        multiline
        rows={14}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={
          "mode ms\nproduct ms-base\nonboarding careerStage=2\nhat 1234 rating=LOVE"
        }
        sx={{
          fontFamily: "monospace",
          "& textarea": {
            fontFamily: "monospace",
            fontSize: 13,
            whiteSpace: "pre",
            overflowWrap: "normal",
            overflowX: "auto",
          },
        }}
      />
    </Box>
  );
};
